import { useState, useEffect } from "react";
import { FaTimes, FaSpinner, FaDownload, FaFileWord, FaExclamationTriangle } from "react-icons/fa";
import { esWord } from "../application/contratoFiles";
import ModalPortal from "./ModalPortal";

// Visor in-app de documentos de contrato.
//  - PDF: se muestra embebido en un iframe (lo renderiza el navegador).
//  - .docx: se convierte a HTML con mammoth (en el navegador, sin terceros).
//  - .doc (formato viejo binario): no se puede previsualizar, se ofrece descargar.
export default function DocumentoViewer({ meta, onClose }) {
  const [html, setHtml] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const isWord = esWord(meta);
  const isDocx = meta?.ext === "docx" || /\.docx($|\?)/i.test(meta?.name || "");

  useEffect(() => {
    if (!meta?.url || !isWord || !isDocx) return;
    let cancelado = false;
    setLoading(true);
    setError(null);
    setHtml(null);

    Promise.all([
      fetch(meta.url).then((r) => {
        if (!r.ok) throw new Error("No se pudo descargar el documento");
        return r.arrayBuffer();
      }),
      import("mammoth/mammoth.browser").then((m) => m.default || m),
    ])
      .then(([arrayBuffer, mammoth]) => mammoth.convertToHtml({ arrayBuffer }))
      .then((res) => {
        if (!cancelado) setHtml(res.value || "<p>(El documento está vacío)</p>");
      })
      .catch((e) => {
        if (!cancelado) {
          const msg = /Failed to fetch|CORS|NetworkError/i.test(e.message || "")
            ? "El navegador no pudo leer el archivo (CORS). Descargalo para verlo, o configurá CORS en el bucket."
            : e.message || "No se pudo abrir el documento";
          setError(msg);
        }
      })
      .finally(() => {
        if (!cancelado) setLoading(false);
      });

    return () => { cancelado = true; };
  }, [meta, isWord, isDocx]);

  if (!meta) return null;

  return (
    <ModalPortal>
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-2 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-5xl h-[92vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-4 sm:px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <h3 className="font-semibold text-gray-800 dark:text-gray-100 truncate">
            {meta.name || "Documento del contrato"}
          </h3>
          <div className="flex items-center gap-2 flex-shrink-0">
            <a
              href={meta.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <FaDownload className="w-3 h-3" /> Descargar
            </a>
            <button
              onClick={onClose}
              className="p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <FaTimes />
            </button>
          </div>
        </div>

        {/* Contenido */}
        <div className="flex-1 overflow-auto bg-gray-100 dark:bg-gray-900">
          {/* PDF */}
          {!isWord && (
            <iframe title={meta.name || "documento"} src={meta.url} className="w-full h-full border-0" />
          )}

          {/* Word .docx */}
          {isWord && isDocx && (
            <>
              {loading && (
                <div className="flex flex-col items-center justify-center h-full text-gray-500 gap-3">
                  <FaSpinner className="w-6 h-6 animate-spin" />
                  <p className="text-sm">Abriendo documento...</p>
                </div>
              )}
              {error && !loading && (
                <div className="flex flex-col items-center justify-center h-full text-center px-6 gap-3">
                  <FaExclamationTriangle className="w-7 h-7 text-amber-500" />
                  <p className="text-sm text-gray-600 dark:text-gray-300 max-w-md">{error}</p>
                  <a
                    href={meta.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm px-4 py-2 bg-primary hover:bg-secondary text-white rounded-lg"
                  >
                    <FaDownload className="w-3.5 h-3.5" /> Descargar documento
                  </a>
                </div>
              )}
              {html && !loading && !error && (
                <div className="documento-html bg-white text-gray-900 max-w-3xl mx-auto my-4 sm:my-6 p-6 sm:p-10 shadow rounded">
                  <div dangerouslySetInnerHTML={{ __html: html }} />
                </div>
              )}
            </>
          )}

          {/* .doc viejo: no previsualizable */}
          {isWord && !isDocx && (
            <div className="flex flex-col items-center justify-center h-full text-center px-6 gap-3">
              <FaFileWord className="w-8 h-8 text-blue-600" />
              <p className="text-sm text-gray-600 dark:text-gray-300 max-w-md">
                Este documento está en formato <b>.doc</b> (Word antiguo) y no se puede previsualizar.
                Descargalo para abrirlo en Word. Si lo guardás como <b>.docx</b> y lo volvés a subir, se podrá ver acá.
              </p>
              <a
                href={meta.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm px-4 py-2 bg-primary hover:bg-secondary text-white rounded-lg"
              >
                <FaDownload className="w-3.5 h-3.5" /> Descargar documento
              </a>
            </div>
          )}
        </div>
      </div>

      {/* Estilos del HTML convertido desde Word */}
      <style>{`
        .documento-html h1 { font-size: 1.5rem; font-weight: 700; margin: 1rem 0 .5rem; }
        .documento-html h2 { font-size: 1.25rem; font-weight: 700; margin: 1rem 0 .5rem; }
        .documento-html h3 { font-size: 1.1rem; font-weight: 600; margin: .75rem 0 .5rem; }
        .documento-html p { margin: .5rem 0; line-height: 1.6; }
        .documento-html ul, .documento-html ol { margin: .5rem 0 .5rem 1.5rem; list-style: revert; }
        .documento-html table { border-collapse: collapse; width: 100%; margin: .75rem 0; }
        .documento-html td, .documento-html th { border: 1px solid #d1d5db; padding: .4rem .6rem; }
        .documento-html strong { font-weight: 700; }
        .documento-html img { max-width: 100%; height: auto; }
        .documento-html a { color: #2563eb; text-decoration: underline; }
      `}</style>
    </div>
    </ModalPortal>
  );
}
